// Order sets, apply side (PROD-2712, release 1). The wire vocabulary for applying a set from the
// A/P rail and for the post-ship measurement (PROD-2673 AC5).
//
// PINNED CONTRACT, shared with the Patients service (PROD-2711, built in parallel to exactly these
// names). `order-set-apply-api.contract.test.ts` pins them. Do not rename without the backend.
//
// The read-side additions (`orderable`, `notOrderableReason`, `optionalCount`, `notOrderableCount`,
// the `education` type) now live on the canonical types in `order-set-api.ts` (PROD-2714); the
// aliases below keep the apply side's names pointing at them, never at a second copy.
//
//   POST /api/Patients/OrderSets/{id}/applications
//        { encounterId, patientId, version, appliedItemIds } → 201 { applicationId }
//   POST /api/Patients/OrderSets/applications/{applicationId}/events
//        { type, itemId?, resolution? } → 204
import type { OrderSetDetail, OrderSetItem, OrderSetItemType, OrderSetSummary } from './order-set-api';

/** The apply side reads the canonical shapes (optional additions: missing = orderable / 0). */
export type OrderSetApplyItemType = OrderSetItemType;
export type OrderSetApplyItem = OrderSetItem;
export type OrderSetApplyDetail = OrderSetDetail;
export type OrderSetApplySummary = OrderSetSummary;

/** `false` only when the server says so. A missing value is orderable. */
export function isItemOrderable(item: Pick<OrderSetApplyItem, 'orderable'>): boolean {
  return item.orderable !== false;
}

/** `notOrderableCount`, defaulting a missing value to 0. */
export function notOrderableCountOf(set: Pick<OrderSetApplySummary, 'notOrderableCount'>): number {
  const n = set.notOrderableCount;
  return typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : 0;
}

/** Body of `POST /OrderSets/{id}/applications`. `appliedItemIds` = the items actually added. */
export interface CreateOrderSetApplicationBody {
  encounterId: string;
  patientId: string;
  version: number;
  appliedItemIds: string[];
}

/** 201 body of `POST /OrderSets/{id}/applications`. */
export interface OrderSetApplicationResult {
  applicationId: string;
}

export const ORDER_SET_EVENT_TYPES = [
  'optional_added',
  'order_anyway',
  'undo',
  'remove_all',
  'needs_diagnosis_resolved',
  'edited_before_sign',
  'removed_before_sign',
] as const;
export type OrderSetEventType = (typeof ORDER_SET_EVENT_TYPES)[number];

/** Only for `needs_diagnosis_resolved`. */
export const ORDER_SET_RESOLUTIONS = ['confirmed_suggested', 'picked_other', 'left_unresolved'] as const;
export type OrderSetResolution = (typeof ORDER_SET_RESOLUTIONS)[number];

/**
 * Body of `POST /OrderSets/applications/{applicationId}/events`. No patient data and no free text:
 * the type, the set item's own id and, for one type, a fixed resolution string.
 */
export interface OrderSetEventBody {
  type: OrderSetEventType;
  itemId?: string;
  resolution?: OrderSetResolution;
}
